import { useMemo, useState } from "react";

import type { CartItem, FulfillmentType } from "../types/cart";
import type { StoreAddress } from "../types/marketplace";
import { formatCurrency, formatNumber } from "../utils/formatters";

interface CartDrawerProps {
  isOpen: boolean;
  items: CartItem[];
  onClearStore: (storeKey: string) => void;
  onClose: () => void;
  onQuantityChange: (itemId: string, quantity: number) => void;
  onRemove: (itemId: string) => void;
}

interface CartStoreGroup {
  addresses: StoreAddress[];
  items: CartItem[];
  key: string;
  name: string;
  phone?: string | null;
  pickupAddress?: string | null;
  quantity: number;
  total: number;
  whatsapp?: string | null;
}

function normalizePhone(value?: string | null): string {
  if (!value) {
    return "";
  }

  const digits = value.replace(/\D/g, "");

  if (!digits) {
    return "";
  }

  if (digits.startsWith("00")) {
    return digits.substring(2);
  }

  if (digits.startsWith("54")) {
    return digits;
  }

  return `54${digits.replace(/^0+/, "")}`;
}

function normalizedAddresses(item: CartItem): StoreAddress[] {
  const addresses = item.product.clientAddresses ?? [];

  if (addresses.length > 0) {
    return addresses.map((address, index) => ({
      ...address,
      id: address.id ?? `${index}-${address.address}`
    }));
  }

  if (item.product.clientPickupAddress) {
    return [
      {
        address: item.product.clientPickupAddress,
        id: "main",
        mapUrl: null,
        name: "Sucursal principal"
      }
    ];
  }

  return [];
}

function productKey(item: CartItem): string {
  return item.product.id ?? item.product.name;
}

function storeKey(item: CartItem): string {
  return String(item.product.clientId ?? item.product.clientName ?? "tienda");
}

function groupItems(items: CartItem[]): CartStoreGroup[] {
  const groups = new Map<string, CartStoreGroup>();

  items.forEach((item) => {
    const key = storeKey(item);
    const existingGroup = groups.get(key);
    const subtotal = item.unitPrice * item.quantity;

    if (existingGroup) {
      existingGroup.items.push(item);
      existingGroup.quantity += item.quantity;
      existingGroup.total += subtotal;
      return;
    }

    const addresses = normalizedAddresses(item);

    groups.set(key, {
      addresses,
      items: [item],
      key,
      name: item.product.clientName ?? "Tienda",
      phone: item.product.clientPhone,
      pickupAddress: addresses[0]?.address ?? item.product.clientPickupAddress ?? null,
      quantity: item.quantity,
      total: subtotal,
      whatsapp: item.product.clientWhatsapp ?? item.product.clientPhone
    });
  });

  return Array.from(groups.values());
}

function addressText(address?: StoreAddress): string {
  if (!address) {
    return "A coordinar con la tienda";
  }

  return `${address.name}: ${address.address}`;
}

function buildMessage(
  group: CartStoreGroup,
  fulfillment: FulfillmentType,
  selectedAddress: StoreAddress | undefined,
  note: string
): string {
  const productLines = group.items
    .map((item, index) => {
      const subtotal = item.unitPrice * item.quantity;
      const lines = [
        `${formatNumber(index + 1)}. ${item.product.name}`,
        `   Código: ${item.product.code ?? productKey(item)}`,
        `   Cantidad: ${formatNumber(item.quantity)}`,
        `   Precio unitario: ${formatCurrency(item.unitPrice)}`,
        `   Subtotal: ${formatCurrency(subtotal)}`
      ];

      if (item.variantLabel) {
        lines.splice(2, 0, `   Combinación: ${item.variantLabel}`);
      }

      return lines.join("\n");
    })
    .join("\n\n");

  const deliveryLines =
    fulfillment === "pickup"
      ? [
          "Forma de entrega: Retiro por sucursal",
          `Sucursal de retiro: ${addressText(selectedAddress)}`
        ]
      : [
          "Forma de entrega: Envío a coordinar",
          `Datos para envío: ${note.trim() || "A coordinar con la tienda"}`
        ];

  const optionalNote =
    fulfillment === "pickup" && note.trim() ? [`Observaciones: ${note.trim()}`] : [];

  return [
    `Hola ${group.name}, quiero consultar este pedido desde Mika Market:`,
    "",
    productLines,
    "",
    `Total estimado: ${formatCurrency(group.total)}`,
    ...deliveryLines,
    ...optionalNote,
    "",
    "Aclaración: vi estos productos en Mika Market. Entiendo que la compra, el pago, el envío, los cambios y la garantía se coordinan directamente con la tienda."
  ].join("\n");
}

function CartDrawer({
  isOpen,
  items,
  onClearStore,
  onClose,
  onQuantityChange,
  onRemove
}: CartDrawerProps) {
  const [copiedStoreKey, setCopiedStoreKey] = useState<string>("");
  const [fulfillmentByStore, setFulfillmentByStore] = useState<Record<string, FulfillmentType>>({});
  const [notesByStore, setNotesByStore] = useState<Record<string, string>>({});
  const [selectedAddressByStore, setSelectedAddressByStore] = useState<Record<string, string>>({});
  const groups = useMemo(() => groupItems(items), [items]);
  const totalQuantity = items.reduce((total, item) => total + item.quantity, 0);
  const totalAmount = items.reduce((total, item) => total + item.quantity * item.unitPrice, 0);

  if (!isOpen) {
    return null;
  }

  const handleCopy = async (storeKeyValue: string, message: string) => {
    if (!navigator.clipboard) {
      return;
    }

    await navigator.clipboard.writeText(message);
    setCopiedStoreKey(storeKeyValue);
    window.setTimeout(() => setCopiedStoreKey(""), 1800);
  };

  return (
    <div className="cart-overlay" role="dialog" aria-modal="true">
      <aside className="cart-drawer">
        <header className="cart-drawer-header">
          <div>
            <small>Carrito por tienda</small>
            <h2>Enviar pedido por WhatsApp</h2>
            <p>
              {formatNumber(totalQuantity)} productos seleccionados · Total estimado{" "}
              {formatCurrency(totalAmount)}
            </p>
          </div>
          <button aria-label="Cerrar carrito" onClick={onClose} type="button">
            x
          </button>
        </header>

        <section className="marketplace-cart-note">
          Mika Market es un marketplace: mostramos productos de tiendas independientes. La compra
          final se coordina directamente con cada marca, fuera de esta web, para respetar sus medios
          de pago, envíos, cambios y garantías.
        </section>

        {groups.length === 0 ? (
          <div className="cart-empty">
            <h3>Tu carrito está vacío</h3>
            <p>Agregá productos y los vamos a ordenar automáticamente por tienda.</p>
          </div>
        ) : (
          <div className="cart-store-list">
            {groups.map((group) => {
              const fulfillment = fulfillmentByStore[group.key] ?? "delivery";
              const selectedAddressId =
                selectedAddressByStore[group.key] ?? group.addresses[0]?.id ?? "";
              const selectedAddress = group.addresses.find(
                (address) => address.id === selectedAddressId
              );
              const note = notesByStore[group.key] ?? "";
              const message = buildMessage(group, fulfillment, selectedAddress, note);
              const whatsappNumber = normalizePhone(group.whatsapp ?? group.phone);
              const whatsappUrl = whatsappNumber
                ? `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`
                : "";

              return (
                <article className="cart-store-card" key={group.key}>
                  <div className="cart-store-head">
                    <div>
                      <small>{formatNumber(group.quantity)} productos</small>
                      <h3>{group.name}</h3>
                      <p>Total estimado {formatCurrency(group.total)}</p>
                    </div>
                    <button onClick={() => onClearStore(group.key)} type="button">
                      Vaciar tienda
                    </button>
                  </div>

                  <div className="cart-items">
                    {group.items.map((item) => (
                      <div className="cart-item-row" key={item.id}>
                        <img
                          src={item.variantImage ?? item.product.image}
                          alt={item.product.name}
                          decoding="async"
                          loading="lazy"
                        />
                        <div>
                          <strong>{item.product.name}</strong>
                          {item.variantLabel && <small>{item.variantLabel}</small>}
                          <span>{formatCurrency(item.unitPrice)}</span>
                        </div>
                        <div className="cart-quantity">
                          <button
                            aria-label={`Restar ${item.product.name}`}
                            onClick={() => onQuantityChange(item.id, item.quantity - 1)}
                            type="button"
                          >
                            -
                          </button>
                          <span>{formatNumber(item.quantity)}</span>
                          <button
                            aria-label={`Sumar ${item.product.name}`}
                            onClick={() => onQuantityChange(item.id, item.quantity + 1)}
                            type="button"
                          >
                            +
                          </button>
                        </div>
                        <button
                          className="cart-remove"
                          onClick={() => onRemove(item.id)}
                          type="button"
                        >
                          Quitar
                        </button>
                      </div>
                    ))}
                  </div>

                  <div className="fulfillment-box">
                    <label className="fulfillment-option">
                      <input
                        checked={fulfillment === "delivery"}
                        onChange={() =>
                          setFulfillmentByStore((current) => ({
                            ...current,
                            [group.key]: "delivery"
                          }))
                        }
                        name={`fulfillment-${group.key}`}
                        type="radio"
                      />
                      <span>Envío</span>
                    </label>
                    <label className="fulfillment-option">
                      <input
                        checked={fulfillment === "pickup"}
                        disabled={group.addresses.length === 0}
                        onChange={() =>
                          setFulfillmentByStore((current) => ({
                            ...current,
                            [group.key]: "pickup"
                          }))
                        }
                        name={`fulfillment-${group.key}`}
                        type="radio"
                      />
                      <span>Retiro por sucursal</span>
                    </label>
                  </div>

                  {fulfillment === "pickup" ? (
                    <div className="pickup-box">
                      {group.addresses.length > 0 ? (
                        <>
                          <label htmlFor={`pickup-${group.key}`}>Sucursal de retiro</label>
                          <select
                            id={`pickup-${group.key}`}
                            onChange={(event) =>
                              setSelectedAddressByStore((current) => ({
                                ...current,
                                [group.key]: event.target.value
                              }))
                            }
                            value={selectedAddressId}
                          >
                            {group.addresses.map((address) => (
                              <option key={address.id} value={address.id}>
                                {address.name} - {address.address}
                              </option>
                            ))}
                          </select>
                          <p>{addressText(selectedAddress)}</p>
                        </>
                      ) : (
                        <p>Esta tienda todavía no cargó dirección para retiro.</p>
                      )}
                    </div>
                  ) : (
                    <div className="pickup-box">
                      <label htmlFor={`delivery-${group.key}`}>Dirección o zona de envío</label>
                      <textarea
                        id={`delivery-${group.key}`}
                        onChange={(event) =>
                          setNotesByStore((current) => ({
                            ...current,
                            [group.key]: event.target.value
                          }))
                        }
                        placeholder="Ej.: Palermo, CABA. Coordinar horario por WhatsApp."
                        value={note}
                      />
                    </div>
                  )}

                  {fulfillment === "pickup" && (
                    <div className="pickup-box">
                      <label htmlFor={`note-${group.key}`}>Observaciones</label>
                      <textarea
                        id={`note-${group.key}`}
                        onChange={(event) =>
                          setNotesByStore((current) => ({
                            ...current,
                            [group.key]: event.target.value
                          }))
                        }
                        placeholder="Ej.: Paso a retirar por la tarde."
                        value={note}
                      />
                    </div>
                  )}

                  <div className="cart-actions">
                    {whatsappUrl ? (
                      <a
                        className="whatsapp-order-button"
                        href={whatsappUrl}
                        rel="noreferrer"
                        target="_blank"
                      >
                        Enviar a {group.name}
                      </a>
                    ) : (
                      <button className="whatsapp-order-button disabled" disabled type="button">
                        Sin WhatsApp configurado
                      </button>
                    )}

                    <button
                      className="copy-order-button"
                      onClick={() => handleCopy(group.key, message)}
                      type="button"
                    >
                      {copiedStoreKey === group.key ? "Pedido copiado" : "Copiar pedido"}
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </aside>
    </div>
  );
}

export default CartDrawer;
